import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/react';
import {
  Card,
  Button,
  Modal,
  BodyText,
  Alert,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';

import { useDeletePaymentProfile } from '../../../hooks';
import { deletePaymentProfileSelectors } from '../../../machines/deletePaymentProfile';
import { ErrorList } from '../ErrorList';

type DeletePaymentProfileContentProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: (successUpdate?: boolean) => void;
  /**
   * Payment profile identifier
   */
  paymentProfileId: number;
};

const Content = ({
  onClose,
  displayMode,
  paymentProfileId,
}: DeletePaymentProfileContentProps) => {
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const toastService = useToastMessages();

  const onDeleteSuccess = () => {
    toastService.show({
      message: t('payment_profile.delete_payment_method_success'),
      level: 'info',
    });
    onClose(true);
  };

  const deletePaymentProfileService = useDeletePaymentProfile({
    paymentProfileId,
    onDeleteSuccess,
  });
  const { subscriptions: usedSubscriptions, errorCode } = useSelector(
    deletePaymentProfileService,
    deletePaymentProfileSelectors.getError
  );
  const { isLoading, hasError } = useSelector(
    deletePaymentProfileService,
    deletePaymentProfileSelectors.getDeletePaymentProfileStatus
  );
  const isPaymentProfileUsed =
    errorCode === 'PaymentProfileInUseByOneOrMoreSubscriptions';

  return (
    <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
      <Card.Header>
        <Modal.Title
          onClose={onClose}
          title={t('payment_profile.remove_payment_method')}
        />
      </Card.Header>
      <Card.Body>
        {isPaymentProfileUsed && (
          <ErrorList
            listDescription={t(
              'payment_profile.this_payment_method_is_connected_to'
            )}
            bottomText={t(
              'payment_profile.choose_new_payment_method_before_delete'
            )}
            items={usedSubscriptions}
            listTitle={t(
              'payment_profile.unable_to_delete_payment_method_title'
            )}
          />
        )}
        {!hasError && (
          <BodyText>
            {t('payment_profile.delete_payment_method_description')}
            <BodyText as={'span'} color={'danger.main'}>
              {t('payment_profile.this_action_cannot_be_undone')}
            </BodyText>
          </BodyText>
        )}
        {hasError && !isPaymentProfileUsed && (
          <Alert color="danger" title={t('payment_profile.unexpected_problem')}>
            <Alert.TextMessage>
              {t('payment_profile.unable_to_delete_payment_method')}
            </Alert.TextMessage>
          </Alert>
        )}
      </Card.Body>
      <Card.Footer>
        <Button.Group alignment="right">
          <Button
            disabled={isLoading}
            color="secondary"
            label={t('close', { ns: 'common' })}
            onClick={() => onClose(false)}
          />
          {!hasError && (
            <Button
              disabled={hasError}
              color="danger"
              isLoading={isLoading}
              label={t('payment_profile.delete_it')}
              onClick={() =>
                deletePaymentProfileService.send({
                  type: 'DELETE_PAYMENT_PROFILE',
                })
              }
            />
          )}
        </Button.Group>
      </Card.Footer>
    </Card>
  );
};

export default Content;
